import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Store as StoreIcon,
  PersonPin as PersonPinIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  RecordVoiceOver as RecordVoiceOverIcon,
  Camera,
  CalendarMonth,
  Groups2,
  Groups,
  VpnKey,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/useAuth';
import { hasPermission, Permissions } from '../utils/permissions';
import { LayoutWrapper } from '../styles/globalStyle';
import ProfileEditModal from '../components/modals/profile/profileEditDialog';
import ChangePasswordDialog from '../components/modals/profile/ChangePasswordDialog';

const drawerWidth = 240;
const collapsedDrawerWidth = 80;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const storedUser = localStorage.getItem('user');

  const loginUserData = storedUser ? JSON.parse(storedUser) : null;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [openProfile, setOpenProfile] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
      permission: Permissions.DASHBOARD_VIEW,
    },
    {
      text: 'Users',
      icon: <PeopleIcon />,
      path: '/users',
      permission: Permissions.USER_READ,
    },
    {
      text: 'Agenda',
      icon: <EventIcon />,
      path: '/agenda',
      permission: Permissions.EVENT_READ,
    },
    {
      text: 'Exhibitors',
      icon: <StoreIcon />,
      path: '/exhibitors',
      permission: Permissions.EXHIBITOR_READ,
    },
    {
      text: 'Delegates',
      icon: <PersonPinIcon />,
      path: '/delegates',
      permission: Permissions.DELEGATE_READ,
    },
    {
      text: 'Visitors',
      icon: <VisibilityIcon />,
      path: '/visitors',
      permission: Permissions.USER_READ,
    },
    {
      text: 'Speakers',
      icon: <RecordVoiceOverIcon />,
      path: '/speakers',
      permission: Permissions.USER_READ,
    },
    // {
    //   text: 'Roles',
    //   icon: <SecurityIcon />,
    //   path: '/roles',
    //   permission: Permissions.SETTINGS_READ,
    // },
    {
      text: 'Conference Session',
      icon: <Groups2 />,
      path: '/conference',
      permission: Permissions.SESSION_READ,
    },
    {
      text: 'ESC Calender',
      icon: <CalendarMonth />,
      path: '/esccalender',
      permission: Permissions.ESC_READ,
    },
    {
      text: 'Floor Image',
      icon: <Camera />,
      path: '/floorimage',
      permission: Permissions.FLOOR_PLAN_READ,
    },
    {
      text: 'Guests',
      icon: <Groups />,
      path: '/guest',
      permission: Permissions.SESSION_READ,
    },
  ].filter(
    item => hasPermission(user, item.permission)
    // TODO: need to change after
    // hasPermission(user, item.permission)
  );
  const pageTitleMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/users': 'Users',
    '/agenda': 'Agenda',
    '/exhibitors': 'Exhibitors',
    '/delegates': 'Delegates',
    '/visitors': 'Visitors',
    '/speakers': 'Speakers',
    '/conference': 'Conference Session',
    '/esccalender': 'ESC Calendar',
    '/floorimage': 'Floor Image',
    '/guest': 'Guests',
    '/notifications': 'Notifications',
  };

  const getPageTitle = () => {
    const path = location.pathname;

    // Static match
    if (pageTitleMap[path]) {
      return pageTitleMap[path];
    }

    // Dynamic routes
    if (path.startsWith('/exhibitors/')) {
      return 'Exhibitor Details';
    }

    if (path.startsWith('/users/')) {
      return 'User Details';
    }

    return 'Dashboard';
  };

  // const getPageTitle = () => {
  //   const currentPath = location.pathname;
  //   const menuItem = menuItems.find(item => item.path === currentPath);
  //   return menuItem ? menuItem.text : 'Dashboard';
  // };

  const drawer = (isCollapsed = false) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Section */}
      <Box
        sx={{
          p: 3,
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          minHeight: 80,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            bgcolor: '#3b82f6',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mr: isCollapsed ? 0 : 1.5,
          }}
        >
          <Typography
            sx={{ color: 'white', fontSize: '0.875rem', fontWeight: 700 }}
          >
            IS
          </Typography>
        </Box>
        {!isCollapsed && (
          <Box>
            <Typography
              sx={{ fontSize: '1.125rem', fontWeight: 700, color: '#111827' }}
            >
              IndiaSoft
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Admin Panel
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, p: 2 }}>
        <List sx={{ padding: 0 }}>
          {menuItems.map(item => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) {
                    setMobileOpen(false);
                  }
                }}
                sx={{
                  minHeight: 48,
                  justifyContent: isCollapsed ? 'center' : 'initial',
                  px: 2,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    bgcolor: '#dbeafe',
                    color: '#1d4ed8',
                    '& .MuiListItemIcon-root': {
                      color: '#1d4ed8',
                    },
                  },
                  '&:hover': {
                    bgcolor: '#f3f4f6',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isCollapsed ? 0 : 2,
                    justifyContent: 'center',
                    color:
                      location.pathname === item.path ? '#1d4ed8' : '#6b7280',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isCollapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: {
            md: desktopOpen ? drawerWidth : collapsedDrawerWidth,
          },
          flexShrink: { md: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'white',
              borderRight: '1px solid #e5e7eb',
            },
          }}
        >
          {drawer()}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: desktopOpen ? drawerWidth : collapsedDrawerWidth,
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              bgcolor: 'white',
              borderRight: '1px solid #e5e7eb',
              boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            },
          }}
          open
        >
          {drawer(!desktopOpen)}
        </Drawer>
      </Box>

      {/* Main Content */}
      <LayoutWrapper>
        {/* Header */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e5e7eb',
            boxShadow:
              '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          }}
        >
          <Toolbar sx={{ px: 3, py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <IconButton
                onClick={handleDrawerToggle}
                sx={{
                  mr: 2,
                  p: 1,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#f3f4f6' },
                  color: '#6b7280',
                }}
              >
                <MenuIcon />
              </IconButton>
              <Typography
                sx={{
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  color: '#111827',
                  textTransform: 'capitalize',
                }}
              >
                {getPageTitle()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Notifications */}
              <IconButton
                onClick={() => navigate('/notifications')}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#f3f4f6' },
                  color: '#6b7280',
                }}
              >
                {/* <Badge badgeContent={3} color="error">  */}
                <NotificationsIcon />
                {/* </Badge> */}
              </IconButton>

              {/* Profile Section */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: '#111827',
                    }}
                  >
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {user?.role?.name || user?.email || 'admin@indiasoft.org'}
                  </Typography>
                </Box>
                <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: '#3b82f6',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    {user?.firstName?.charAt(0)}
                    {user?.lastName?.charAt(0)}
                  </Avatar>
                </IconButton>
              </Box>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiPaper-root': {
              borderRadius: 2,
              border: '1px solid #e5e7eb',
              boxShadow:
                '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              mt: 1,
              minWidth: 180,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              setOpenProfile(true);
            }}
            sx={{ py: 1.5, px: 2 }}
          >
            <ListItemIcon>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem' }}>Profile</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleProfileMenuClose();
              setOpenChangePassword(true);
            }}
            sx={{ py: 1.5, px: 2 }}
          >
            <ListItemIcon>
              <VpnKey fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem' }}>
              Change Password
            </Typography>
          </MenuItem>

          {/* <MenuItem onClick={handleProfileMenuClose} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem' }}>Settings</Typography>
          </MenuItem> */}
          <Divider />
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, px: 2 }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <Typography sx={{ fontSize: '0.875rem' }}>Logout</Typography>
          </MenuItem>
        </Menu>
        {/* profile open modal */}
        <ProfileEditModal
          open={openProfile}
          onClose={() => setOpenProfile(false)}
          userData={loginUserData}
        />
        {/* change password open modal */}
        <ChangePasswordDialog
          open={openChangePassword}
          onClose={() => setOpenChangePassword(false)}
        />

        {/* Content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            overflow: 'auto',
          }}
        >
          {children}
        </Box>
      </LayoutWrapper>
    </Box>
  );
};

export default Layout;
